/**
 * WS-ELIG B-E4 (constitution closure 2026-10-19) — the acceptance screen's
 * disclosure of a roster-entry adoption.
 *
 * CORE-W:45: where a shop pre-seeded a roster entry, acceptance SHALL adopt it
 * rather than duplicate it, so the work already attributed to it is not
 * orphaned. CORE-B:70: no transfer of a professional identity's records without
 * explicit human authority. The shop's invitation NAMES the entry; the person
 * sees this disclosure and accepts — that acceptance, carrying the disclosed id
 * back (`adopt_roster_entry`), is the authority the server requires.
 */
export interface RosterEntryAdoption {
  technicianId: string;
  name: string;
  workRecords: number;
  profileRecords: number;
}

/** The details endpoint's `roster_entry`, or null when the invitation names none (or it is malformed). */
export function parseRosterEntry(raw: unknown): RosterEntryAdoption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.technician_id !== 'string' || r.technician_id.length === 0) return null;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
  return {
    technicianId: r.technician_id,
    name: typeof r.name === 'string' && r.name.trim() ? r.name.trim() : 'an existing roster entry',
    workRecords: n(r.work_records),
    profileRecords: n(r.profile_records),
  };
}

/** Plain-language disclosure shown BEFORE the person accepts. */
export function describeRosterAdoption(
  entry: RosterEntryAdoption,
  shopName: string,
  /** WS-GAPS2 B-E4: the same disclosure on a Connect offer (the shop sends a new OFFER, not an invitation). */
  via: 'invitation' | 'offer' = 'invitation',
): string[] {
  const shop = shopName || 'This shop';
  const lines = [
    `${shop} already has you on its roster as “${entry.name}”. Joining takes over that entry instead of creating a second one.`,
  ];
  const history = entry.workRecords + entry.profileRecords;
  lines.push(history > 0
    ? `${history} record${history === 1 ? '' : 's'} of work already attributed to that entry will become part of your OptiBay profile.`
    : 'No work has been recorded under that entry yet.');
  lines.push(`If this is not you, do not accept — ask the shop to send a new ${via}.`);
  return lines;
}
